import { FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import BaseModal from '@app/shared/components/modal/BaseModal';
import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import { Toggle } from '@app/shared/components/toggle/Toggle';
import { useLocale } from '@app/core/hooks/useLocale';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { FeatureFlag } from '@core/feature-flags/feature-flag-enums';
import { featureFlagLabels } from '@core/feature-flags/feature-flag-labels';
import {
  getAllFeatureFlags,
  getUnknownServerFeatureFlags,
  setFeatureFlag,
} from '@core/feature-flags/feature-flags';
import {
  getFeatureFlagFormDefaultValues,
  getServerFlagsByValue,
  getUnknownServerFlagsByValue,
  getUserControllableFlags,
} from './feature-flags-modal-helpers';
import styles from './feature-flags-modal.module.scss';

interface FeatureFlagsModalProps {
  onCloseButtonClick: () => void;
}

interface FeatureFlagListProps {
  flags: FeatureFlag[];
  unknownFlags: number[];
}

const FeatureFlagList: FC<FeatureFlagListProps> = ({ flags, unknownFlags }) => {
  const { l } = useLocale();

  const labels = [
    ...flags.map((flag) => l(featureFlagLabels[flag]!)),
    ...unknownFlags.map((flag) => l('_FeatureFlagUnknownFlag', { flagKey: flag })),
  ];

  return (
    <ul className={styles['feature-flags-modal__server-list']}>
      {labels.length === 0 && (
        <li className={styles['feature-flags-modal__empty']}>{l('_None')}</li>
      )}
      {labels.map((label) => (
        <li key={label}>{label}</li>
      ))}
    </ul>
  );
};

export const FeatureFlagsModal: FC<FeatureFlagsModalProps> = ({ onCloseButtonClick }) => {
  const { l } = useLocale();

  const flagStatuses = useMemo(() => getAllFeatureFlags(), []);

  const userControllableFlags = useMemo(
    () => getUserControllableFlags(flagStatuses),
    [flagStatuses]
  );
  const { enabled: serverEnabledFlags, disabled: serverDisabledFlags } = useMemo(
    () => getServerFlagsByValue(flagStatuses),
    [flagStatuses]
  );

  // Sometimes the server might specify flags that the FE doesn't know about. We're going to show
  // them here because it's helpful from a dev POV to see what's going on with backend flags even
  // if the FE doesn't know what they are.
  const unknownServerFlags = useMemo(() => getUnknownServerFeatureFlags(), []);
  const { enabled: unknownEnabledFlags, disabled: unknownDisabledFlags } = useMemo(
    () => getUnknownServerFlagsByValue(unknownServerFlags),
    [unknownServerFlags]
  );

  const defaultValues = useMemo(
    () => getFeatureFlagFormDefaultValues(userControllableFlags, flagStatuses),
    [userControllableFlags, flagStatuses]
  );

  const formMethods = useForm<Partial<Record<FeatureFlag, boolean>>>({
    ...formConfigBase,
    defaultValues,
  });

  // Reads values synchronously (rather than via handleSubmit) so the reload stays inside the
  // click's synchronous call stack - Firefox drops the reload if it's deferred past a microtask.
  const handleApply = () => {
    const values = formMethods.getValues();
    Object.entries(values).forEach(([flag, isEnabled]) =>
      setFeatureFlag(Number(flag), Boolean(isEnabled))
    );
    window.location.reload();
  };

  const serverFlagColumns = [
    {
      heading: l('_FeatureFlagEnabled'),
      flags: serverEnabledFlags,
      unknownFlags: unknownEnabledFlags,
    },
    {
      heading: l('_FeatureFlagDisabled'),
      flags: serverDisabledFlags,
      unknownFlags: unknownDisabledFlags,
    },
  ];

  return (
    <BaseModal
      isOpen
      size={ModalSize.Medium}
      title={l('_FeatureFlags')}
      onCloseButtonClick={onCloseButtonClick}
      buttonConfirmLabel={l('_Reset')}
      buttonCancelLabel={l('_Apply')}
      onConfirm={onCloseButtonClick}
      onCancel={handleApply}>
      <p>{l('_FeatureFlagBodyDescription')}</p>

      <FormProvider {...formMethods}>
        <form>
          <ul className={styles['feature-flags-modal__list']}>
            {userControllableFlags.length === 0 && (
              <li className={styles['feature-flags-modal__empty']}>{l('_None')}</li>
            )}
            {userControllableFlags.map((flag) => (
              <li key={flag} className={styles['feature-flags-modal__list-item']}>
                <Toggle name={String(flag)} label={l(featureFlagLabels[flag]!)} />
              </li>
            ))}
          </ul>
        </form>
      </FormProvider>

      <h3 className={styles['feature-flags-modal__server-heading']}>
        {l('_ServerManagedFeatureFlags')}
      </h3>
      <p>{l('_ServerManagedFeatureFlagsDesc')}</p>

      <div className={styles['feature-flags-modal__server-columns']}>
        {serverFlagColumns.map((column) => (
          <div key={column.heading}>
            <p className={styles['feature-flags-modal__server-column-heading']}>{column.heading}</p>
            <FeatureFlagList flags={column.flags} unknownFlags={column.unknownFlags} />
          </div>
        ))}
      </div>
    </BaseModal>
  );
};
